import React, { useEffect, useState, useCallback, useMemo } from 'react';
import * as Yup from 'yup';
import { Table, Button, Modal, Input, Dropdown, Menu, Typography, Icon, message } from 'antd';

import Form from '../Form';
import { TabHeader } from './styles';

import SegmentsAPI from '../../services/sdks/segments';
import { FiPlusCircle, FiXCircle, FiPlus, FiEdit } from 'react-icons/fi';
import TextArea from 'antd/lib/input/TextArea';

const Segments = ({ data, setData, program, user }) => {
	const [fallback, setFallback] = useState(null);
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [title, setTitle] = useState('');
	const [description, setDescription] = useState('');
	const [editingSegment, setEditingSegment] = useState(null);
	const [pagination, setPagination] = useState({
		current: 1,
		pageSize: 10,
		total: 0,
	});

	const isUploadButtonDisabled = useMemo(() => {
		if (!title || !description) {
			return true;
		}

		return false;
	}, [description, title]);

	const isSharedProgram = useMemo(() => {
		if (!program || !user) {
			return true;
		}

		return program?.userId?._id !== user?._id;
	}, [program, user]);

	const columns = [
		{
			title: 'ID',
			dataIndex: '_id',
			key: 'id',
			render: (_id) => (
				<Typography.Text title={_id} copyable={{ text: _id }}>{`${_id.slice(
					0,
					8
				)}...`}</Typography.Text>
			),
		},
		{
			title: 'Nome',
			key: 'title',
			render: (element) => <span>{element.title}</span>,
		},
		{
			title: 'Descrição',
			key: 'description',
			render: (element) => <span>{element.description}</span>,
		},
		{
			title: 'Ações',
			align: 'right',
			key: 'actions',
			render: (segment) => (
				<Dropdown
					placement='bottomRight'
					overlay={
						<Menu>
							<Menu.Item
								key='edit'
								disabled={!program?.isEditable || isSharedProgram}
								onClick={() => handleOpenEditModal(segment)}>
								<Icon type='edit' /> Editar
							</Menu.Item>

							<Menu.Divider />

							<Menu.Item
								key='delete'
								className='ant-dropdown-menu-item-danger'
								disabled={!program?.isEditable || isSharedProgram}
								onClick={() => {
									Modal.confirm({
										title: 'Excluir elemento?',
										icon: 'exclamation-circle',
										content: 'Essa ação não poderá ser revertida, deseja continuar mesmo assim?',
										okText: 'Excluir',
										onOk: () => handleDeleteSegment({ segmentId: segment._id }),
										okButtonProps: {
											icon: 'delete',
											type: 'danger',
										},
										cancelText: 'Cancelar',
										cancelButtonProps: {
											icon: 'close-circle',
										},
									});
								}}>
								<Icon type='delete' /> Excluir
							</Menu.Item>
						</Menu>
					}>
					<Icon style={{ cursor: 'pointer', fontSize: 20, marginRight: 16 }} type='more' />
				</Dropdown>
			),
		},
	];

	const handleOpenEditModal = useCallback((segment) => {
		setEditingSegment(segment);
		setTitle(segment.title);
		setDescription(segment.description);
		setShowEditModal(true);
	}, []);

	const handleCloseEditModal = useCallback(() => {
		setShowEditModal(false);
		setEditingSegment(null);
		setTitle('');
		setDescription('');
		setFallback(null);
	}, []);

	const fetchData = useCallback(
		async (pageNumber = 1, pageSize = 10) => {
			try {
				const query = `programId=${program?._id}&pageNumber=${pageNumber}&pageSize=${pageSize}`;
				const {
					data: { segments, total },
				} = await SegmentsAPI.getAll({ query });

				setData(segments);
				setPagination((prev) => ({
					...prev,
					current: pageNumber,
					pageSize: pageSize,
					total: total || segments.length,
				}));
			} catch (error) {
				console.error(error);
				message.error(error);
			}
		},
		[program?._id, setData]
	);

	const handleTableChange = useCallback(
		(paginationConfig) => {
			fetchData(paginationConfig.current, paginationConfig.pageSize);
		},
		[fetchData]
	);

	const handleCreateSegment = useCallback(async () => {
		try {
			setFallback('creating-element');
			const payload = { title, description, programId: program._id };
			await SegmentsAPI.create({ payload });

			setTitle('');
			setDescription('');
			setShowCreateModal(false);
			setFallback(null);

			await fetchData(pagination.current, pagination.pageSize);

			return message.success('Quadro criado com sucesso!');
		} catch (error) {
			setFallback(null);

			if (error instanceof Yup.ValidationError) {
				return message.error(error.message);
			}

			const errorMessage = error?.response?.data?.message;

			if (errorMessage) {
				message.error(errorMessage);
			}

			console.error(error);
		}
	}, [title, description, program._id, fetchData, pagination]);

	const handleUpdateSegment = useCallback(async () => {
		try {
			setFallback('updating-element');
			const payload = { title, description };
			const {
				data: { programSegment },
			} = await SegmentsAPI.update({ id: editingSegment._id, payload });

			setData((prev) =>
				prev.map((segment) => (segment._id === editingSegment._id ? programSegment : segment))
			);

			message.success('Quadro atualizado com sucesso!');
			handleCloseEditModal();
		} catch (error) {
			setFallback(null);

			if (error instanceof Yup.ValidationError) {
				return message.error(error.message);
			}

			const errorMessage = error?.response?.data?.message;

			if (errorMessage) {
				message.error(errorMessage);
			}

			console.error(error);
		}
	}, [title, description, editingSegment, setData, handleCloseEditModal]);

	const handleDeleteSegment = useCallback(
		async ({ segmentId }) => {
			try {
				setFallback(`deleting-element-${segmentId}`);

				await SegmentsAPI.delete({ id: segmentId });

				await fetchData(pagination.current, pagination.pageSize);
				setFallback(null);

				return message.success('Quadro deletado com sucesso!');
			} catch (error) {
				console.error(error);
			}
		},
		[fetchData, pagination]
	);

	useEffect(() => {
		if (data === null && program?._id) {
			fetchData(1, 10);
		}
	}, [data, program?._id, fetchData]);

	return (
		<>
			{program?.isEditable && program?.userId?._id === user?._id && (
				<TabHeader>
					<div>
						<span></span>
						<Button onClick={() => setShowCreateModal(true)}>
							<FiPlus /> Novo Quadro
						</Button>
					</div>
				</TabHeader>
			)}

			<Table
				rowKey='_id'
				pagination={{
					...pagination,
					size: 'large',
				}}
				onChange={handleTableChange}
				style={{ border: 'none' }}
				loading={data === null}
				size='middle'
				columns={columns}
				dataSource={data}
				rowClassName='__row'
			/>

			<Modal
				destroyOnClose
				visible={showCreateModal}
				onOk={handleCreateSegment}
				okButtonProps={{
					loading: fallback === 'creating-element',
					disabled: isUploadButtonDisabled,
				}}
				cancelButtonProps={{ disabled: fallback === 'creating-element' }}
				okText='Salvar'
				cancelText={
					<>
						<FiXCircle />
						Cancelar
					</>
				}
				title={
					<>
						<FiPlusCircle />
						Novo Quadro
					</>
				}
				onCancel={() => setShowCreateModal(false)}>
				<Form.Container>
					<Form.Item label='Nome'>
						<Input
							placeholder='Informe um nome para o quadro'
							value={title}
							onChange={({ target: { value } }) => setTitle(value)}
						/>
					</Form.Item>

					<Form.Item label='Descrição'>
						<TextArea
							placeholder='Informe uma descrição para o quadro'
							value={description}
							onChange={({ target: { value } }) => setDescription(value)}
						/>
					</Form.Item>
				</Form.Container>
			</Modal>

			<Modal
				destroyOnClose
				visible={showEditModal}
				onOk={handleUpdateSegment}
				okButtonProps={{
					loading: fallback === 'updating-element',
					disabled: isUploadButtonDisabled,
				}}
				cancelButtonProps={{ disabled: fallback === 'updating-element' }}
				okText='Salvar'
				cancelText={
					<>
						<FiXCircle />
						Cancelar
					</>
				}
				title={
					<>
						<FiEdit />
						Editar Quadro
					</>
				}
				onCancel={handleCloseEditModal}>
				<Form.Container>
					<Form.Item label='Nome'>
						<Input
							placeholder='Informe um nome para o quadro'
							value={title}
							onChange={({ target: { value } }) => setTitle(value)}
						/>
					</Form.Item>

					<Form.Item label='Descrição'>
						<TextArea
							placeholder='Informe uma descrição para o quadro'
							value={description}
							onChange={({ target: { value } }) => setDescription(value)}
						/>
					</Form.Item>
				</Form.Container>
			</Modal>
		</>
	);
};

export default Segments;
